import React, { useEffect, useState } from 'react';
import { auth } from '../../../../../../firebase/config/config';
import { useChatbotContext } from '../../../../../../context/ChatbotContext';
import logger from '../../../../../../utils/logger';
import {
  Code,
  Layout,
  Globe,
  ShoppingCart,
  Layers,
  CheckCircle,
  AlertCircle,
  Copy,
  Settings,
  Palette,
  MessageSquare,
  Zap,
  ChevronDown,
  ChevronRight,
  Check,
  Power
} from 'lucide-react';

// Error Modal Component (Unchanged)
const ErrorModal = ({ isOpen, onClose, error, errorDetails }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-red-600 flex items-center gap-2">
            <AlertCircle size={20} />
            {errorDetails?.usageType === 'chatbots' ? 'Limit Reached' : 'Usage Limit'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">×</button>
        </div>

        <div className="p-6">
          {errorDetails ? (
            <>
              <p className="text-slate-600 mb-4 text-sm">
                You've reached your {errorDetails.usageType === 'chatbots' ? 'chatbot creation' : errorDetails.usageType} limit.
              </p>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 font-medium">Current Usage</span>
                  <span className="font-bold text-slate-800">{errorDetails.currentUsage} / {errorDetails.limit}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 font-medium">Plan</span>
                  <span className="font-bold text-violet-600">{errorDetails.plan || 'Starter'}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-slate-600 text-sm">{error}</p>
          )}
        </div>

        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button
            className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

function Publish({
  assistantName,
  messagePrompt,
  introMessageText,
  botTagline,
  headerColor,
  chatBackgroundColor,
  botMessageBoxColor,
  botMessageTextColor,
  userMessageBoxColor,
  userMessageTextColor,
  openAIVectorStoreID,
  openAIAssistantID,
  openAIAPIKey,
  chatbotRolePrompt,
  aiModelTier = 'standard',
  responseDelay,
  openChatOnLoad,
  chatOpenDelay,
  internalMessagePrompt,
  isPublished,
  showPoweredBy,
  widgetPosition,
  announcement,
  // Toggle Button configuration
  toggleButtonSize,
  toggleButtonIcon,
  customToggleButtonIcon,
  toggleButtonIconSize,
  toggleButtonBackgroundColor,
  toggleButtonIconOffsetX,
  toggleButtonIconOffsetY,
  toggleButtonOutlineEnabled,
  toggleButtonOutlineColor,
  // WooCommerce props (Passthrough for saving)
  wooCommerceApiKey,
  wooCommerceApiSecret,
  wooCommerceStoreUrl,
  wooCommerceEnabled,
  orderTrackingEnabled,
  selectedFiles,
  headerStyle,
  botIconUrl,
  botIconOffsetX,
  botIconOffsetY,
  excludedUrls,
  // Proactive Bubble settings
  proactiveBubbleEnabled,
  proactiveBubbleDelay,
  proactiveBubbleMode,
  proactiveBubbleDefaultMessage,
  // Master Widget Toggle
  widgetEnabled = true,
  setWidgetEnabled,
  // Sound Notification
  soundNotificationEnabled = true,
  humanHandoff,
  officeHours,
  saveConfig,
}) {
  const { refreshChatbots } = useChatbotContext();
  const [chatbotId, setChatbotId] = useState('');
  const [publishError, setPublishError] = useState('');

  // UI State
  const [embeddingType, setEmbeddingType] = useState('dom'); // 'dom', 'iframe'
  const [expandedIntegration, setExpandedIntegration] = useState('manual'); // 'manual', 'shopify', 'wordpress', 'duda'

  // Duda State
  const [dudaMode, setDudaMode] = useState('manual'); // 'manual', 'auto'
  const [dudaSiteId, setDudaSiteId] = useState('');
  const [dudaUsername, setDudaUsername] = useState(''); // Actually API Key/User
  const [dudaPassword, setDudaPassword] = useState(''); // Actually API Password
  const [isInstallingDuda, setIsInstallingDuda] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [copied, setCopied] = useState(false);

  const BASE_URL = window.location.origin;

  useEffect(() => {
    const savedChatbotId = localStorage.getItem('chatbotId');
    if (savedChatbotId) {
      setChatbotId(savedChatbotId);
    }
  }, []);

  // --- Configuration Logic ---

  const validateConfiguration = (config) => {
    if ((config.openAIVectorStoreID || config.openAIAssistantID) && !config.openAIAPIKey) {
      throw new Error('OpenAI API Key is required when using a custom Vector Store or Assistant ID');
    }
  };

  const saveConfiguration = async () => {
    try {
      setPublishError('');
      setErrorDetails(null);
      setShowErrorModal(false);

      // Fetch existing menu items to preserve them
      let existingMenuItems = [];
      try {
        if (chatbotId) {
          const existingConfigResponse = await fetch(`${BASE_URL}/api/chatbot?chatbotId=${chatbotId}`);
          if (existingConfigResponse.ok) {
            const existingConfigData = await existingConfigResponse.json();
            existingMenuItems = existingConfigData.config?.menuItems || [];
          }
        }
      } catch (error) { }

      const config = {
        assistantName,
        messagePrompt,
        introMessageText,
        botTagline,
        headerColor,
        chatBackgroundColor,
        botMessageBoxColor,
        botMessageTextColor,
        userMessageBoxColor,
        userMessageTextColor,
        openAIVectorStoreID,
        openAIAssistantID,
        openAIAPIKey,
        chatbotRolePrompt,
        aiModelTier,
        responseDelay,
        openChatOnLoad,
        chatOpenDelay,
        internalMessagePrompt,
        showPoweredBy,
        widgetPosition,
        announcement: {
          text: announcement?.text || '',
          enabled: announcement?.enabled || false,
          style: announcement?.style || 'info'
        },
        toggleButtonSize,
        toggleButtonIcon: toggleButtonIcon || '',
        customToggleButtonIcon: customToggleButtonIcon || '',
        toggleButtonIconSize,
        toggleButtonBackgroundColor,
        toggleButtonIconOffsetX,
        toggleButtonIconOffsetY,
        toggleButtonOutlineEnabled,
        toggleButtonOutlineColor,
        wooCommerceEnabled,
        orderTrackingEnabled,
        wooCommerceApiKey: wooCommerceEnabled ? wooCommerceApiKey : '',
        wooCommerceApiSecret: wooCommerceEnabled ? wooCommerceApiSecret : '',
        wooCommerceStoreUrl: wooCommerceEnabled ? wooCommerceStoreUrl : '',
        selectedFiles,
        headerStyle,
        botIconUrl,
        botIconOffsetX,
        botIconOffsetY,
        excludedUrls,
        menuItems: existingMenuItems,
        proactiveBubbleEnabled: proactiveBubbleEnabled !== false,
        proactiveBubbleDelay: proactiveBubbleDelay || 5,
        proactiveBubbleMode: proactiveBubbleMode || 'ai',
        proactiveBubbleDefaultMessage: proactiveBubbleDefaultMessage || 'Have a question? Ask me anything!',
        // Master Widget Toggle
        widgetEnabled,
        // Sound Notification
        soundNotificationEnabled: soundNotificationEnabled !== false,
        // Human Handoff
        humanHandoff,
        officeHours,
      };

      validateConfiguration(config);

      const user = auth.currentUser;
      const userId = user ? user.uid : null;
      const token = user ? await user.getIdToken() : null;

      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${BASE_URL}/api/chatbot`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ chatbotId, config, userId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429 && errorData.usageType) {
          setErrorDetails({
            usageType: errorData.usageType,
            currentUsage: errorData.currentUsage,
            limit: errorData.limit,
            plan: errorData.plan
          });
          setShowErrorModal(true);
        } else {
          setPublishError(errorData.error || 'Failed to save configuration');
        }
        return;
      }

      refreshChatbots();
      // Could add a toast success here
    } catch (error) {
      logger.error('Error saving configuration:', error);
      setPublishError(error.message);
    }
  };

  // --- Embed Code Generation ---

  const generateEmbedCode = () => {
    if (embeddingType === 'iframe') {
      const positionStyle = widgetPosition === 'left' ? 'left: 20px;' : 'right: 20px;';
      return `<iframe id="myChatbotIframe" src="${BASE_URL}/embed/chatbot?chatbotId=${chatbotId}" style="position: fixed; bottom: 20px; ${positionStyle} width: 60px; height: 60px; border: none; z-index: 9999;"></iframe>
<script>
window.addEventListener('message', (event) => {
    if (!event.data) return;
    if (event.data.type === 'RESIZE_IFRAME') {
    const iframe = document.getElementById('myChatbotIframe');
    iframe.style.width = event.data.width + 'px';
    iframe.style.height = event.data.height + 'px';
    }
});
</script>`;
    } else {
      // DOM Embed
      return `<!-- SmushChat Widget -->
<script src="${BASE_URL}/chatbot-loader.js"></script>
<script>
  document.addEventListener('DOMContentLoaded', function() {
    SmushChatWidget.init('${chatbotId}');
  });
</script>`;
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generateEmbedCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDudaAutoInstall = async () => {
    if (!dudaSiteId || !dudaUsername || !dudaPassword) return;
    setIsInstallingDuda(true);
    setPublishError('');

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      const response = await fetch(`${BASE_URL}/api/duda/install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          siteId: dudaSiteId,
          scriptContent: generateEmbedCode(),
          username: dudaUsername,
          password: dudaPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to install script');
      }

      // Success
      setPublishError(''); // Clear any previous errors
      // You might want a success toast here instead of generic alert, but for now:
      alert(data.message || 'Duda installation successful!');

    } catch (error) {
      console.error('Duda install error:', error);
      setPublishError(error.message);
    } finally {
      setIsInstallingDuda(false);
    }
  };

  // --- UI Components ---

  const IntegrationItem = ({ id, name, icon, description, children }) => {
    const isExpanded = expandedIntegration === id;

    return (
      <div className={`rounded-xl border transition-all duration-300 overflow-hidden ${isExpanded ? 'bg-white border-violet-200 shadow-md' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
        <button
          onClick={() => setExpandedIntegration(isExpanded ? null : id)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-4">
            <div className={`p-2.5 rounded-lg transition-colors ${isExpanded ? 'bg-violet-100 text-violet-600' : 'bg-slate-100 text-slate-500'}`}>
              {icon}
            </div>
            <div>
              <h3 className={`font-semibold text-sm ${isExpanded ? 'text-violet-900' : 'text-slate-800'}`}>{name}</h3>
              <p className="text-xs text-slate-500 mt-0.5 font-medium">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`text-xs px-2 py-1 rounded-full font-medium ${isExpanded ? 'bg-violet-50 text-violet-700' : 'bg-slate-50 text-slate-400'}`}>
              {isExpanded ? 'Active' : 'Connect'}
            </div>
            {isExpanded ? <ChevronDown size={18} className="text-violet-400" /> : <ChevronRight size={18} className="text-slate-300" />}
          </div>
        </button>

        {isExpanded && (
          <div className="border-t border-slate-100 bg-slate-50/30 p-5 animate-in slide-in-from-top-2 fade-in duration-300">
            {children}
          </div>
        )}
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden font-sans">
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        error={publishError}
        errorDetails={errorDetails}
      />

      {/* LEFT / CENTER PANEL: Configuration */}
      <div className="flex-1 h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden relative">
        <div className="p-5 border-b border-slate-100 bg-white sticky top-0 z-10">
          <h2 className="font-bold text-lg text-slate-800">Publish</h2>
          <p className="text-xs text-slate-500">Choose a method to deploy your assistant</p>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-4">

          {/* 1. Manual Embed (Always first) */}
          <div className={`rounded-xl border transition-all duration-200 overflow-hidden ${expandedIntegration === 'manual' ? 'bg-white border-slate-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
            <button
              onClick={() => setExpandedIntegration(expandedIntegration === 'manual' ? null : 'manual')}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2.5 rounded-lg transition-colors ${expandedIntegration === 'manual' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  <Code size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Universal Embed Code</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Add to any website with a simple script</p>
                </div>
              </div>
              {expandedIntegration === 'manual' ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-300" />}
            </button>

            {expandedIntegration === 'manual' && (
              <div className="border-t border-slate-100 p-6 animate-in slide-in-from-top-1 fade-in duration-200">
                {/* Toggle Type */}
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={() => setEmbeddingType('dom')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${embeddingType === 'dom'
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`}
                  >
                    Modern (DOM)
                  </button>
                  <button
                    onClick={() => setEmbeddingType('iframe')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${embeddingType === 'iframe'
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`}
                  >
                    Legacy (Iframe)
                  </button>
                </div>

                {/* Code Block */}
                <div className="relative group">
                  <div className="absolute top-0 left-0 w-full h-8 bg-gradient-to-b from-slate-900/5 to-transparent rounded-t-xl pointer-events-none" />
                  <pre className="bg-slate-900 text-slate-300 p-5 rounded-xl text-xs font-mono overflow-x-auto border border-slate-800 shadow-inner leading-relaxed">
                    {generateEmbedCode()}
                  </pre>
                  <div className="absolute top-3 right-3">
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/10 text-white text-xs font-bold rounded-lg transition-all"
                    >
                      {copied ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
                  <AlertCircle size={12} />
                  Place this code just before the closing <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-700 font-mono text-[10px]">&lt;/body&gt;</code> tag.
                </p>
              </div>
            )}
          </div>

          <div className="h-px w-full bg-slate-100 my-4" />
          <h4 className="px-1 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Platform Integrations</h4>

          {/* 2. Shopify */}
          <IntegrationItem
            id="shopify"
            name="Shopify"
            icon={<ShoppingCart size={20} />}
            description="One-click installation for your store"
          >
            <div className="flex flex-col items-center justify-center py-8 text-center bg-slate-50/50 rounded-xl border border-slate-100">
              <div className="w-16 h-16 bg-white border border-slate-200 rounded-2xl flex items-center justify-center mb-4">
                <ShoppingCart size={32} className="text-emerald-600" />
              </div>
              <h4 className="font-bold text-slate-900 mb-1">Connect Your Store</h4>
              <p className="text-sm text-slate-500 max-w-xs mb-6 leading-relaxed">Install the SmushChat app on your Shopify store to automatically deploy your chatbot.</p>
              <button className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all">
                <Zap size={16} /> Install on Shopify
              </button>
            </div>
          </IntegrationItem>

          {/* 3. WordPress */}
          <IntegrationItem
            id="wordpress"
            name="WordPress"
            icon={<Layout size={20} />}
            description="Plugin for WordPress & WooCommerce"
          >
            <div className="flex flex-col items-center justify-center py-8 text-center bg-slate-50/50 rounded-xl border border-slate-100">
              <div className="w-16 h-16 bg-white border border-slate-200 rounded-2xl flex items-center justify-center mb-4">
                <Layout size={32} className="text-blue-600" />
              </div>
              <h4 className="font-bold text-slate-900 mb-1">Download Plugin</h4>
              <p className="text-sm text-slate-500 max-w-xs mb-6 leading-relaxed">Download our official WordPress plugin to integrate seamlessly with your site.</p>
              <button className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all">
                <Layers size={16} /> Download Plugin
              </button>
            </div>
          </IntegrationItem>

          {/* 4. Duda */}
          <IntegrationItem
            id="duda"
            name="Duda"
            icon={<Globe size={20} />}
            description="Native integration for Duda sites"
          >
            <div className="flex flex-col gap-5 p-1">
              {/* Mode Switcher */}
              <div className="flex p-1 bg-slate-100 rounded-lg self-start">
                <button
                  onClick={() => setDudaMode('manual')}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${dudaMode === 'manual' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Manual (Global)
                </button>
                <button
                  onClick={() => setDudaMode('auto')}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${dudaMode === 'auto' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Automatic
                </button>
              </div>

              {dudaMode === 'manual' ? (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                  <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg text-blue-600 mt-0.5">
                        <Globe size={16} />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-800 text-sm">One-Time Installation</h4>
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                          You don't need to add the script to every page! Just add it once to the <strong>Head HTML</strong> section to cover your entire site.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 relative group">
                    <pre className="text-slate-300 font-mono text-[10px] overflow-x-auto whitespace-pre-wrap break-all pr-12">
                      {generateEmbedCode()}
                    </pre>
                    <button
                      onClick={handleCopy}
                      className="absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                    >
                      {copied ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
                    </button>
                  </div>

                  <ol className="text-xs text-slate-500 space-y-2 list-decimal list-inside pl-2">
                    <li>Log in to your Duda Dashboard.</li>
                    <li>Go to <strong>Settings</strong> {'>'} <strong>Head HTML</strong>.</li>
                    <li>Paste the code above into the <strong>Body End HTML</strong> (recommended) or Head HTML.</li>
                    <li>Republish your site.</li>
                  </ol>
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-3">
                    <div className="bg-blue-50 text-blue-800 p-3 rounded-lg text-xs mb-2 flex items-start gap-2">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      <div>
                        <span className="font-bold block mb-0.5">Not your login details!</span>
                        You need your <strong>API Access</strong> credentials. Go to your Duda Dashboard → White Label → API Access.
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Duda API User (Key)</label>
                      <input
                        type="text"
                        value={dudaUsername}
                        onChange={(e) => setDudaUsername(e.target.value)}
                        placeholder="e.g. 5d8f9a2b..."
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:border-violet-500 transition-colors font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Duda API Password</label>
                      <input
                        type="password"
                        value={dudaPassword}
                        onChange={(e) => setDudaPassword(e.target.value)}
                        placeholder="Your unique API password"
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:border-violet-500 transition-colors font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Duda Site UUID</label>
                      <input
                        type="text"
                        value={dudaSiteId}
                        onChange={(e) => setDudaSiteId(e.target.value)}
                        placeholder="e.g. 8a199239..."
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:border-violet-500 transition-colors"
                      />
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={handleDudaAutoInstall}
                        disabled={!dudaSiteId || !dudaUsername || !dudaPassword || isInstallingDuda}
                        className="w-full px-4 py-2 bg-violet-600 text-white font-bold text-xs rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                      >
                        {isInstallingDuda ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Zap size={14} />}
                        Install Script Automatically
                      </button>
                    </div>

                    <p className="text-[10px] text-slate-400 text-center">
                      We do not store your credentials. They are used once to install the script.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </IntegrationItem>

        </div>
      </div>

      {/* RIGHT PANEL: Review & Publish */}
      <div className="w-[340px] h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden flex-shrink-0">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-bold text-slate-800 text-sm tracking-tight flex items-center gap-2">
            <CheckCircle size={16} className="text-violet-500" />
            Review Configuration
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-8">

          {/* Section: Identity */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                {botIconUrl ? (
                  <img src={botIconUrl} alt="Bot" className="w-full h-full object-cover" />
                ) : (
                  <MessageSquare size={24} className="text-slate-300" />
                )}
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">{assistantName || 'Untitled'}</h3>
                <p className="text-xs text-slate-500 line-clamp-2 mt-1">{botTagline || 'No tagline configured'}</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-slate-100" />

          {/* Section: Appearance */}
          <div>
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Palette size={12} /> Theme & Colors
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50/50 border border-slate-100">
                <span className="text-xs font-medium text-slate-600">Header Color</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-400">{headerColor}</span>
                  <div className="w-4 h-4 rounded-full border border-black/5 shadow-sm" style={{ backgroundColor: headerColor }} />
                </div>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50/50 border border-slate-100">
                <span className="text-xs font-medium text-slate-600">Bot Message</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-400">{botMessageBoxColor}</span>
                  <div className="w-4 h-4 rounded-full border border-black/5 shadow-sm" style={{ backgroundColor: botMessageBoxColor }} />
                </div>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50/50 border border-slate-100">
                <span className="text-xs font-medium text-slate-600">User Message</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-400">{userMessageBoxColor}</span>
                  <div className="w-4 h-4 rounded-full border border-black/5 shadow-sm" style={{ backgroundColor: userMessageBoxColor }} />
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-slate-100" />

          {/* Section: Settings */}
          <div>
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Settings size={12} /> Configuration
            </h4>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-[10px] text-slate-400 font-bold uppercase mb-1">Model</div>
                <div className="text-xs font-bold text-slate-700 truncate capitalize">
                  {openAIVectorStoreID ? 'Vector Store (Responses API)' : (openAIAssistantID ? 'Custom Assistant' : aiModelTier || 'Standard')}
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-[10px] text-slate-400 font-bold uppercase mb-1">Language</div>
                <div className="text-xs font-bold text-slate-700 capitalize">English</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-[10px] text-slate-400 font-bold uppercase mb-1">Position</div>
                <div className="text-xs font-bold text-slate-700 capitalize">{widgetPosition || 'Right'}</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-[10px] text-slate-400 font-bold uppercase mb-1">Intro Message</div>
                <div className="text-xs font-bold text-slate-700">{introMessageText ? 'Enabled' : 'Disabled'}</div>
              </div>
            </div>

            {/* Additional Toggles Summary */}
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-medium">Auto-Open Chat</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${openChatOnLoad ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                  {openChatOnLoad ? 'ON' : 'OFF'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-medium">Proactive Bubble</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${proactiveBubbleEnabled !== false ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                  {proactiveBubbleEnabled !== false ? 'ON' : 'OFF'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-medium">Show Branding</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${showPoweredBy ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                  {showPoweredBy ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>

            {/* Role Prompt Snippet */}
            <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-[10px] text-slate-400 font-bold uppercase mb-2">Role Instructions</div>
              <p className="text-[11px] text-slate-600 italic line-clamp-3 leading-relaxed">
                "{chatbotRolePrompt || 'No role prompt configured...'}"
              </p>
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="p-5 border-t border-slate-100 bg-white z-10 space-y-4">
          {publishError && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100">
              {publishError}
            </div>
          )}

          {/* Master Widget Toggle */}
          <div className={`p-3 rounded-xl border transition-all duration-200 ${widgetEnabled
            ? 'bg-green-50/50 border-green-200'
            : 'bg-red-50/50 border-red-200'
            }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg transition-colors ${widgetEnabled ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                  }`}>
                  <Power size={14} />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-700">Widget Status</div>
                  <div className={`text-[10px] font-medium ${widgetEnabled ? 'text-green-600' : 'text-red-600'}`}>
                    {widgetEnabled ? 'Active on client website' : 'Disabled - Not showing'}
                  </div>
                </div>
              </div>
              <button
                onClick={async () => {
                  const newValue = !widgetEnabled;
                  setWidgetEnabled(newValue);
                  // Immediately save to ensure toggle takes effect
                  if (saveConfig) {
                    await saveConfig({ widgetEnabled: newValue });
                  }
                }}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${widgetEnabled ? 'bg-green-500' : 'bg-red-400'
                  }`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${widgetEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
              </button>
            </div>
            {!widgetEnabled && (
              <p className="text-[10px] text-red-500 mt-2 pl-8">
                The widget won't appear on client websites until re-enabled.
              </p>
            )}
          </div>

          <button
            onClick={saveConfiguration}
            className="w-full flex items-center justify-center gap-2 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-sm shadow-sm transition-all active:scale-[0.98] group"
          >
            <div className="relative">
              <Globe size={16} className={`transition-all duration-300 ${isPublished ? 'scale-0 opacity-0 absolute' : 'scale-100 opacity-100'}`} />
              <Check size={16} className={`transition-all duration-300 ${isPublished ? 'scale-100 opacity-100' : 'scale-0 opacity-0 absolute'}`} />
            </div>
            {isPublished ? 'Update Changes' : 'Publish Chatbot'}
          </button>

          <div className="flex items-center justify-center gap-2">
            <div className={`w-2 h-2 rounded-full ${!widgetEnabled ? 'bg-red-500' : (isPublished ? 'bg-green-500 animate-pulse' : 'bg-amber-400')
              }`} />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
              {!widgetEnabled ? 'Widget Disabled' : (isPublished ? 'Live & Active' : 'Draft Mode')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Publish;
